import { D, dec } from '@makila/fiscal';
/**
 * Código GS1 das embalagens (DataMatrix): (01) GTIN-14, (17) validade AAMMDD,
 * (10) lote. Lê o formato do leitor (FNC1 = carácter GS, \x1d, a fechar os
 * campos variáveis) e o escrito com parênteses. Dia 00 = último dia do mês.
 */
export function parseGs1(raw) {
    let s = raw.trim().replace(/^\]d2|^\]C1|^\]Q3/, '');
    if (s.startsWith('('))
        s = s.replace(/\((\d{2})\)/g, '\x1d$1').replace(/^\x1d/, '');
    const out = { gtin: null, expiry: null, lot: null };
    let i = 0, found = false;
    while (i < s.length) {
        if (s[i] === '\x1d') {
            i++;
            continue;
        }
        const ai = s.slice(i, i + 2);
        if (ai === '01') {
            out.gtin = s.slice(i + 2, i + 16);
            i += 16;
            found = true;
        }
        else if (ai === '17') {
            const v = s.slice(i + 2, i + 8);
            if (!/^\d{6}$/.test(v))
                return null;
            const y = 2000 + Number(v.slice(0, 2)), m = Number(v.slice(2, 4));
            let d = Number(v.slice(4, 6));
            if (m < 1 || m > 12)
                return null;
            if (d === 0)
                d = new Date(Date.UTC(y, m, 0)).getUTCDate();
            out.expiry = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            i += 8;
            found = true;
        }
        else if (ai === '10' || ai === '21') {
            const end = s.indexOf('\x1d', i + 2);
            const v = s.slice(i + 2, end < 0 ? undefined : end);
            if (ai === '10')
                out.lot = v;
            i = end < 0 ? s.length : end;
            found = true;
        }
        else
            return found ? out : null;
    }
    if (out.gtin && !/^\d{14}$/.test(out.gtin))
        return null;
    return found ? out : null;
}
/** Pode vender-se hoje? Vencido (validade antes de hoje) ou recolhido, não. */
export function lotProblem(l, today) {
    if (l.recalled)
        return `O lote ${l.number} foi recolhido: não pode ser vendido`;
    if (l.expiry < today)
        return `O lote ${l.number} está vencido (${l.expiry.split('-').reverse().join('/')}): não pode ser vendido`;
    return null;
}
/** Faixa de alerta da validade: vencido, ou a 30/60/90 dias. */
export function expiryBand(expiry, today) {
    if (expiry < today)
        return 'expired';
    const days = Math.round((Date.parse(`${expiry}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
    return days <= 30 ? 30 : days <= 60 ? 60 : days <= 90 ? 90 : null;
}
/**
 * FEFO: tira a quantidade dos lotes válidos com saldo, do que vence primeiro.
 * O que não couber fica em `rest` (sem lote) — nunca sai de um lote vencido
 * ou recolhido.
 */
export function fefoAllocate(lots, qty, today) {
    let left = dec(qty);
    const take = [];
    const ok = lots.filter((l) => !lotProblem(l, today) && dec(l.quantity).greaterThan(0))
        .sort((a, b) => a.expiry.localeCompare(b.expiry) || a.number.localeCompare(b.number));
    for (const l of ok) {
        if (left.lessThanOrEqualTo(0))
            break;
        const q = D.min(left, dec(l.quantity));
        take.push({ lotId: l.id, quantity: q.toString() });
        left = left.minus(q);
    }
    return { take, rest: D.max(left, 0).toString() };
}
/** Validade de uma receita (decisão de 21/10/2026): 30 dias, salvo doente crónico com o farmacêutico. */
export const RX_VALID_DAYS = 30;
/** Regras da farmácia numa venda; devolve a primeira violação (ou null). */
export function pharmacyProblem(lines, rx, today) {
    for (const l of lines) {
        if (l.regulatedPrice && !dec(l.discountPct ?? 0).isZero())
            return `${l.description}: preço regulado, não leva desconto (art. 64 do Decreto 16/2023)`;
        if (l.substitutedFrom && l.requiresPrescription && !l.substitutedFrom.prescriberConsent)
            return `${l.description}: substituição de medicamento receitado sem o consentimento do prescritor (art. 14)`;
    }
    if (!lines.some((l) => l.requiresPrescription || l.controlled))
        return null;
    if (!rx)
        return 'Esta venda tem medicamentos sujeitos a receita: registe a receita';
    if (!rx.number.trim() || !rx.prescriber.trim())
        return 'Receita: indique o número e o prescritor';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rx.date) || rx.date > today)
        return 'Receita: data inválida';
    if (!Number.isInteger(rx.vias) || rx.vias < 1 || rx.vias > 6)
        return 'Receita: número de vias inválido';
    const age = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${rx.date}T00:00:00Z`)) / 86_400_000);
    if (age > RX_VALID_DAYS && !(rx.chronic && rx.pharmacistId))
        return `Receita com mais de ${RX_VALID_DAYS} dias: só para doente crónico, com o farmacêutico responsável`;
    if (lines.some((l) => l.controlled)) {
        if (!rx.patient?.trim() || !rx.patientId?.trim())
            return 'Controlo especial: identifique o doente (nome e documento)';
        if (!rx.prescriberLicence?.trim())
            return 'Controlo especial: indique a cédula do prescritor';
        if (!rx.pharmacistId)
            return 'Controlo especial: precisa da autorização do farmacêutico responsável';
    }
    return null;
}
//# sourceMappingURL=pharmacy.js.map